import React, { useEffect, useMemo, useState } from "react";

import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import {
  SwitchVertical01,
  SwitchVertical02,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import {
  Cryptocurrency02,
  Diamond01,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Finance & eCommerce";
import { Hourglass01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Time";
import {
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import {
  WEIGHT_BOLD,
  WEIGHT_NORMAL,
} from "@hl/base-components/lib/theme/typography";
import { OfferCriteriaKind } from "@hl/shared-features/lib/apollo/graphql.generated";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import MarketplaceFilterSortBar from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBar";
import MarketplacePrice from "@hl/shared-features/lib/features/marketplace/components/MarketplacePrice";
import OfferTypeFilter, {
  offerTypeOptions,
} from "@hl/shared-features/lib/features/marketplace/components/OfferTypeFilter";
import CancelBidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/CancelBidModal";
import { useUserOffersQuery } from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { UserOffer } from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import usePercentDifference from "@hl/shared-features/lib/hooks/usePercentDifference";
import { SortingOption } from "@hl/shared-features/lib/utils/sortingOptions";
import {
  Anchor,
  Box,
  Button,
  createStyles,
  Group,
  Stack,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs, { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfiniteScroll from "react-infinite-scroll-component";

import { getDetailPageUrl, getMintPageUrl } from "~config";

import {
  OrderSortOptions,
  SortDirection,
} from "../../apollo/graphql.generated";
import { OnLoadProps } from "../profile/UserTabs";

import { Loading } from "./Collected";

extend(relativeTime);
extend(advancedFormat);
extend(utc);
extend(timezone);

const useStyles = createStyles({
  link: {
    textDecoration: "none",
  },
});

const sortingOptions: SortingOption<OrderSortOptions>[] = [
  {
    value: "price_desc",
    label: "Highest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.DESC,
    icon: SwitchVertical01,
  },
  {
    value: "price_asc",
    label: "Lowest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.ASC,
    icon: SwitchVertical02,
  },
  {
    value: "exp_asc",
    label: "Expiring soonest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.ASC,
    icon: Cryptocurrency02,
  },
  {
    value: "exp_desc",
    label: "Expiring latest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.DESC,
    icon: Hourglass01,
  },
  {
    value: "highest_above_floor",
    label: "Highest above floor",
    sortBy: OrderSortOptions.HIGHEST_ABOVE_FLOOR,
    sortDirection: SortDirection.DESC,
    icon: Diamond01,
  },
];

export const SentOffers = ({ onLoad }: OnLoadProps) => {
  const theme = useMantineTheme();
  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 250);
  const [sorting, setSorting] = useState<string | null>(
    sortingOptions[0].value
  );
  const [offerType, setOfferType] = useState<OfferCriteriaKind>(
    offerTypeOptions[0].value
  );
  const selectedSort = useMemo(
    () =>
      sortingOptions.find((sortingOption) => sortingOption.value === sorting),
    [sorting]
  );

  const { data, fetchMore, loading, refetch, error } = useUserOffersQuery({
    variables: {
      tokenOrCollectionName: debouncedSearchString,
      sortDirection: selectedSort!.sortDirection,
      sortBy: selectedSort!.sortBy,
      after: null,
      first: 10,
      criteriaKind: offerType || null,
    },
  });

  useEffect(() => {
    const totalCount = data?.getUserOffers.pageInfo.totalCount;
    if (totalCount != null) {
      onLoad?.({ totalCount });
    }
  }, [data?.getUserOffers.pageInfo.totalCount]);

  const nextCursor = data?.getUserOffers?.pageInfo.endCursor;
  const hasNextPage = data?.getUserOffers?.pageInfo?.hasNextPage;

  const bids: UserOffer[] = useMemo(
    () => data?.getUserOffers.edges || [],
    [data]
  );

  const tableHeaders = (
    <tr>
      <th>Item</th>
      <th>Offer Price</th>
      <th>Expires In</th>
      <th></th>
    </tr>
  );

  const tableRows = bids.map((bid, i) => (
    <SentOffersRow bid={bid} key={i} refetch={refetch} />
  ));

  const typeFilter = (
    <OfferTypeFilter
      offerType={offerType as OfferCriteriaKind}
      setOfferType={setOfferType}
    />
  );

  return (
    <Stack>
      <MarketplaceFilterSortBar
        setSorting={setSorting}
        sorting={sorting}
        hasFilterToggle={false}
        sortingOptions={sortingOptions}
        setSearchString={setSearchString}
        customViewElement={typeFilter}
        searchString={searchString}
      />
      {loading ? (
        <Loading />
      ) : bids.length ? (
        <InfiniteScroll
          dataLength={bids.length}
          next={() =>
            hasNextPage &&
            fetchMore({
              variables: { after: nextCursor },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                fetchMoreResult!.getUserOffers.edges = [
                  ...(previousResult?.getUserOffers?.edges ?? []),
                  ...(fetchMoreResult?.getUserOffers?.edges ?? []),
                ];
                return fetchMoreResult;
              },
            })
          }
          hasMore={!!hasNextPage}
          loader={<Loading />}
        >
          <Table
            verticalSpacing="md"
            miw={1200}
            sx={{
              "thead tr th": {
                color: theme.colors.secondaryText[0],
                paddingLeft: 0,
                fontSize: 14,
                fontWeight: WEIGHT_NORMAL,
                paddingTop: 8,
              },
              "tbody tr td": {
                paddingLeft: 0,
              },
              "tbody tr td:last-child": {
                paddingRight: 0,
              },
            }}
          >
            <thead>{tableHeaders}</thead>
            <tbody>{tableRows}</tbody>
          </Table>
        </InfiniteScroll>
      ) : !!error ? (
        <NoResults
          title="Something went wrong"
          description="Please try again later"
        />
      ) : (
        <NoResults
          description={
            debouncedSearchString
              ? "Try adjusting your filter settings"
              : "Once you do, they'll appear here."
          }
          title={
            debouncedSearchString ? "No offers found" : "No offers created yet"
          }
        />
      )}
    </Stack>
  );
};

function SentOffersRow({
  bid,
  refetch,
}: {
  bid: UserOffer;
  refetch: () => void;
}) {
  const { classes } = useStyles();
  const {
    price,
    floorPrice,
    tokens,
    collection,
    quantityRemaining,
    attributeKey,
    attributeValue,
    criteriaKind,
  } = bid;
  const { diffWording } = usePercentDifference(
    price.amount.native,
    floorPrice,
    true
  );
  const { pushModal } = useModalStack();
  const token = tokens?.[0];
  const imageUrl = token ? token.imageUrl : collection?.imageUrl ?? "";
  const collectionUrl = collection ? getMintPageUrl(collection) : "";
  const tokenUrl =
    collection && token
      ? getDetailPageUrl(collection, token.id)
      : collectionUrl;
  const fp = floorPrice && floorPrice != "0" ? parseFloat(floorPrice) : null;

  return (
    <tr key={bid.createdAt}>
      <td>
        <Stack spacing={4}>
          <MarketplacePrice price={bid.price} />
          {fp && (
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              <FormatCrypto
                amount={fp}
                onlyText
                symbol={bid.price.currency.symbol}
                decimals={bid.price.currency.decimals}
              />{" "}
              floor ({diffWording})
            </Text>
          )}
        </Stack>
      </td>
      <td>
        <Group noWrap>
          <Box p={8} bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}>
            <ResizedImage src={imageUrl} width={64} height={64} fit="contain" />
          </Box>
          <Stack spacing={4}>
            <Anchor href={tokenUrl} className={classes.link}>
              <Group spacing="xs">
                <Anchor href={tokenUrl} className={classes.link}>
                  <Text fw={WEIGHT_BOLD}>
                    {criteriaKind === "token" && token?.name}
                    {criteriaKind === "attribute" &&
                      `Trait: ${attributeKey} ${attributeValue}`}
                    {criteriaKind === "collection" && "Collection offer"}
                  </Text>
                </Anchor>
                {quantityRemaining > 1 && <Text>x{quantityRemaining}</Text>}
              </Group>
              <Text size="sm" fw={WEIGHT_BOLD}></Text>
            </Anchor>
            <Anchor href={collectionUrl} className={classes.link}>
              <Text size="sm">{collection?.name}</Text>
            </Anchor>
          </Stack>
        </Group>
      </td>
      <td>
        {bid.expiredAt && (
          <Stack spacing={4}>
            <Text>{dayjs(bid.expiredAt).format("MMM D @ h:mma z")}</Text>
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              {dayjs().to(bid.expiredAt)}
            </Text>
          </Stack>
        )}
      </td>
      <td align="right">
        <Button
          w="100%"
          size="lg"
          px={12}
          maw="160px"
          onClick={() =>
            !!collection &&
            pushModal(
              <CancelBidModalHighlight
                collectionData={{ ...collection, chainId: bid.chainId }}
                imageUrl={imageUrl}
                id={bid.reservoirId}
                floorPrice={bid.floorPrice ?? undefined}
                tokenId={token?.id}
                onCancel={() => setTimeout(() => refetch(), 5000)}
              />,
              {
                size: "lg",
                title: <Text fw={WEIGHT_BOLD}>Cancel your offer</Text>,
              }
            )
          }
        >
          <Group spacing="xs">Manage offer</Group>
        </Button>
      </td>
    </tr>
  );
}
